import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import {
  RiArrowDownSLine,
  RiArrowLeftDoubleLine,
  RiArrowRightDoubleLine,
  RiCalendarLine,
} from "@remixicon/react";
import { addWeeks, subWeeks } from "date-fns";
import { startOfWeek } from "date-fns/startOfWeek";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { DatePicker } from "@mui/x-date-pickers-pro";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import ShippingWeeklyKpiCards from "../../components/Shipping/Analytics/ShippingWeeklyKpiCards";
import ShippingStatusDetailsTable from "../../components/Shipping/ShippingStatusDetailsTable";
import ShippingStatusSummaryChart from "../../components/Shipping/ShippingStatusSummaryChart";
import ShippingStatusSummaryTable from "../../components/Shipping/ShippingStatusSummaryTable";
import ShippingSummaryByWeek from "../../components/Shipping/ShippingSummaryByWeek";
import ShowActiveDeliveriesBtn from "../../components/Shipping/Tools/ShowActiveDeliveries";
import ShowActiveFGsBtn from "../../components/Shipping/Tools/ShowActiveFGs";
import StructureChecks from "../../components/Compliance/StructureChecks";
import { useShippingDataCoordinator } from "../../hooks/useShippingDataCoordinator";
import { formatDateHumanReadable, formatDateYYYYMMDD } from "utils/formatting";
dayjs.extend(utc);
dayjs.extend(timezone);

const ShippingWeeklyPage = () => {
  const navigate = useNavigate();
  const { week_id } = useParams<{ week_id?: string }>();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const datePickerAnchorRef = useRef<HTMLDivElement>(null);

  // Determine the correct starting date based on the URL or current date.
  // All calculations are in UTC.
  const getInitialDate = (): Date => {
    if (week_id) {
      const parsedDate = dayjs.tz(week_id, "UTC");
      const isAlreadyWeekStart = parsedDate.day() === 1;
      const weekStart = startOfWeek(parsedDate.toDate(), { weekStartsOn: 1 });
      return isAlreadyWeekStart ? parsedDate.toDate() : weekStart;
    }
    // If no week_id, calculate Monday of the current week in UTC
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  };

  const [dateRangeStart, setDateRangeStart] = useState(getInitialDate);

  // The end of the week is 6 days after the start
  const [dateRangeEnd, setDateRangeEnd] = useState(() => {
    const end = new Date(dateRangeStart);
    end.setUTCDate(end.getUTCDate() + 6);
    return end;
  });

  const dateRangeStartReadable = formatDateHumanReadable(dateRangeStart);
  const dateRangeEndReadable = formatDateHumanReadable(dateRangeEnd);
  const dateStartFormatted = formatDateYYYYMMDD(dateRangeStart);
  const dateEndFormatted = formatDateYYYYMMDD(dateRangeEnd);
  // Use the coordinator hook to pre-warm the cache and coordinate fetches.
  const { isLoading, isError } = useShippingDataCoordinator(
    dateStartFormatted,
    dateEndFormatted
  );

  useEffect(() => {
    document.title = `Shipping: ${dateRangeStartReadable} - ${dateRangeEndReadable}`;
  }, [dateRangeStartReadable, dateRangeEndReadable]);

  // Update URL when dateRangeStart changes
  useEffect(() => {
    navigate(`/shipping/weekly/${dateStartFormatted}`, {
      replace: true,
    });
  }, [dateStartFormatted, navigate]);

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      // Correctly set the start of the week to Monday
      const newStart = startOfWeek(newDate, { weekStartsOn: 1 });
      setDateRangeStart(newStart);
      const newEnd = new Date(newStart);
      newEnd.setUTCDate(newEnd.getUTCDate() + 6);
      setDateRangeEnd(newEnd);
    }
    setIsPickerOpen(false);
  };

  const handlePreviousWeek = () => {
    setDateRangeStart((prev) => subWeeks(prev, 1));
    setDateRangeEnd((prev) => subWeeks(prev, 1));
  };

  const handleCurrentWeek = () => {
    const newStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    setDateRangeStart(newStart);
    const newEnd = new Date(newStart);
    newEnd.setUTCDate(newEnd.getUTCDate() + 6);
    setDateRangeEnd(newEnd);
  };

  const handleNextWeek = () => {
    setDateRangeStart((prev) => addWeeks(prev, 1));
    setDateRangeEnd((prev) => addWeeks(prev, 1));
  };

  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  const handleUpdateTimestamp = useCallback((timestamp: number | null) => {
    setLastUpdated(timestamp);
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Shipping for {dateRangeStartReadable} - {dateRangeEndReadable}
          </Typography>
          {isLoading && (
            <Chip label="Loading Data..." color="default" size="small" />
          )}
          {isError && (
            <Chip label="Error Loading Data" color="error" size="small" />
          )}
        </Box>
        <Box
          display="flex"
          justifyContent={{ xs: "center", sm: "center", md: "flex-end" }}
          flexDirection={{ xs: "row" }}
          alignItems="center"
          gap={2}
          sx={{ mb: 2.5 }}
        >
          <Button
            variant="contained"
            onClick={handlePreviousWeek}
            aria-label="Previous week"
            sx={{
              fontSize: { xs: "0.8rem", sm: "1rem" },
            }}
          >
            <RiArrowLeftDoubleLine />
          </Button>
          <ButtonGroup variant="contained" ref={datePickerAnchorRef}>
            <Button
              onClick={handleCurrentWeek}
              sx={{
                fontSize: { xs: "0.8rem", sm: "1rem" },
              }}
            >
              Current Week
            </Button>
            <Button
              size="small"
              onClick={() => setIsPickerOpen(true)}
              aria-label="open date picker"
              sx={{ px: 1 }}
            >
              <RiCalendarLine />
            </Button>
          </ButtonGroup>
          <DatePicker
            open={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            value={dateRangeStart}
            onChange={handleDateChange}
            minDate={new Date(Date.UTC(2023, 0, 1))}
            slots={{
              field: () => null,
            }}
            slotProps={{
              popper: {
                anchorEl: datePickerAnchorRef.current,
                placement: "bottom-end",
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleNextWeek}
            aria-label="Next week"
            sx={{
              fontSize: { xs: "0.8rem", sm: "1rem" },
            }}
          >
            <RiArrowRightDoubleLine />
          </Button>
        </Box>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<RiArrowDownSLine />}
            aria-controls="shipping-weekly-overview-content"
            id="shipping-weekly-overview-header"
          >
            <Typography variant="h5">Overview</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ShippingSummaryByWeek
              dateStart={dateStartFormatted}
              dateEnd={dateEndFormatted}
            />
            <ShippingWeeklyKpiCards
              dateStart={dateStartFormatted}
              dateEnd={dateEndFormatted}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<RiArrowDownSLine />}
            aria-controls="shipping-weekly-compliance-content"
            id="shipping-weekly-compliance-header"
          >
            <Typography variant="h5">Format & Structure Compliance</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <StructureChecks
              dateStart={dateStartFormatted}
              dateEnd={dateEndFormatted}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<RiArrowDownSLine />}
            aria-controls="shipping-weekly-tools-content"
            id="shipping-weekly-tools-header"
          >
            <Typography variant="h5">Shipping Tools</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", gap: 1 }}>
              <ShowActiveDeliveriesBtn
                dateStart={dateStartFormatted}
                dateEnd={dateEndFormatted}
              />
              <ShowActiveFGsBtn
                dateStart={dateStartFormatted}
                dateEnd={dateEndFormatted}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<RiArrowDownSLine />}
            aria-controls="shipping-weekly-status-content"
            id="shipping-weekly-status-header"
          >
            <Typography variant="h5">Status</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", md: "row" },
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  flexBasis: { sm: "100%", md: "50%" },
                  display: "flex",
                  flexDirection: "column",
                  margin: { sm: "0px", md: "30px" },
                }}
              >
                <ShippingStatusSummaryChart
                  dateStart={dateStartFormatted}
                  dateEnd={dateEndFormatted}
                />
              </Box>

              <Box
                sx={{
                  flexBasis: { sm: "100%", md: "50%" },
                  display: "flex",
                  flexDirection: "column",
                  margin: { sm: "0px", md: "30px" },
                  alignItems: "center",
                }}
              >
                <ShippingStatusSummaryTable
                  dateStart={dateStartFormatted}
                  dateEnd={dateEndFormatted}
                  showUpdateTimestamp={true}
                  onUpdateTimestamp={handleUpdateTimestamp}
                />
              </Box>
            </Box>

            <ShippingStatusDetailsTable
              dateStart={dateStartFormatted}
              dateEnd={dateEndFormatted}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    </LocalizationProvider>
  );
};

export default ShippingWeeklyPage;
